import React from 'react';
import { Loading } from '@/ui/components/atoms/Loading';
import { ControlsContainer } from '@/ui/components/molecules/ControlsContainer';
import { ExpertCard } from '../ExpertCard';
import { useExperts } from '../../hooks/useExperts';
import type { Expert } from '../../types/expert.types';
import './ExpertsList.styles.css';

interface ExpertsListProps {
  onExpertSelect: (expert: Expert) => void;
}

export const ExpertsList: React.FC<ExpertsListProps> = ({ onExpertSelect }) => {
  const {
    experts,
    totalExperts,
    isLoading,
    error,
    filters,
    updateFilter,
    refetch
  } = useExperts();

  if (isLoading) {
    return (
      <div className="experts-list-container">
        <Loading 
          size="large" 
          text="Chargement des experts..."
          fullScreen={false}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="experts-list-container">
        <div className="experts-list-error">
          {error instanceof Error ? error.message : 'Une erreur est survenue'}
        </div>
      </div>
    );
  }

  return (
    <div className="experts-list-page-container">
      {/* Header - Matching payment-header style */}
      <div className="experts-list-header">
        <div className="experts-list-header-content">
          <div className="experts-list-title-row">
            <div>
              <h1 className="experts-list-title">
                Gestion des Experts
              </h1>
              <p className="experts-list-subtitle">
                Visualisez la charge de travail de tous les experts
              </p>
            </div>
            <div className="experts-list-action-wrapper">
              <div className="experts-list-stats">
                <span className="experts-list-stat">
                  Total: <strong>{totalExperts}</strong> expert{totalExperts > 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="experts-list-content">
        <div className="experts-list-content-inner">
        {/* Controls and filters */}
        <div className="experts-list__controls-wrapper">
          <ControlsContainer
            searchQuery={filters.searchQuery}
            onSearchChange={(value) => updateFilter('searchQuery', value)}
            sortBy={filters.sortBy}
            onSortChange={(value) => updateFilter('sortBy', value)}
            sortOrder={filters.sortOrder}
            onSortOrderChange={(value) => updateFilter('sortOrder', value)}
            sortOptions={[
              { value: 'dossiers', label: 'Charge de travail' },
              { value: 'name', label: 'Nom' },
              { value: 'nextWeek', label: 'RDV semaine prochaine' },
              { value: 'weekAfter', label: 'RDV dans 2 semaines' }
            ]}
            activeFilter={filters.activeFilter}
            onFilterChange={(value) => updateFilter('activeFilter', value)}
            filterOptions={[
              { key: 'all', label: 'Tous' },
              { key: 'highWorkload', label: 'Charge élevée' },
              { key: 'available', label: 'Disponible' }
            ]}
            showFilters={true}
            resultsCount={experts.length}
            searchPlaceholder="Rechercher par nom, email..."
            className="experts-list__controls-container"
          />
        </div>

        {/* Experts grid */}
        {experts.length > 0 ? (
          <div className="experts-list__grid">
            {experts.map(expert => (
              <ExpertCard
                key={expert.fm_name}
                expert={expert}
                onClick={() => onExpertSelect(expert)}
              />
            ))}
          </div>
        ) : (
          <div className="experts-list__empty">
            <svg 
              width="64" 
              height="64" 
              viewBox="0 0 64 64" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="experts-list__empty-icon"
            >
              <path 
                d="M32 8C18.745 8 8 18.745 8 32C8 45.255 18.745 56 32 56C45.255 56 56 45.255 56 32C56 18.745 45.255 8 32 8Z" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <path 
                d="M20 24C20 24 24 20 32 20C40 20 44 24 44 24M24 36H24.02M40 36H40.02" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <path 
                d="M25 44C25 44 27 40 32 40C37 40 39 44 39 44" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
            <h3 className="experts-list__empty-title">
              Aucun expert trouvé
            </h3>
            <p className="experts-list__empty-message">
              {filters.searchQuery || filters.activeFilter !== 'all'
                ? 'Aucun expert ne correspond aux critères de recherche'
                : 'Aucun expert n\'a été configuré dans le système'}
            </p>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};